"use client"

import { errorMessage } from '@/lib/errors';
import * as React from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "sonner"
import { Separator } from "@/components/ui/separator"
import { useTranslations } from "next-intl"

interface LoginFormProps {
  onLoginSuccess: (username: string, password: string) => void;
  onSSOLogin?: () => void;
  useSSO?: boolean;
}

export function LoginForm({ onLoginSuccess, onSSOLogin, useSSO = false }: LoginFormProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [showLocalLogin, setShowLocalLogin] = React.useState(!useSSO);
  const t = useTranslations("login");

  const form = useForm({
    defaultValues: {
      username: "",
      password: "",
    },
  })

  async function onSubmit(data: { username: string; password: string }) {
    setIsLoading(true);

    try {
      await onLoginSuccess(data.username, data.password);
      // Don't show success toast here - SIP registration success/failure is handled in auth-context
      // The login function will show appropriate toasts for SIP registration status
    } catch (error: unknown) {
      // Error toast is already shown in auth-context login function for SIP registration failures
      // Only show error here if it's not a SIP registration error (which is already handled)
      if (!errorMessage(error)?.includes('SIP registration')) {
        toast.error(errorMessage(error) || t("failed"));
      }
    } finally {
      setIsLoading(false);
    }
  }

  // If SSO is enabled, show SSO-first UI
  if (useSSO && !showLocalLogin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("titleSignIn")}</CardTitle>
          <CardDescription>
            {t("descriptionSSO")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            className="w-full"
            onClick={onSSOLogin}
            disabled={isLoading}
          >
            {t("signInWithSSO")}
          </Button>

          <div className="relative" role="separator" aria-orientation="horizontal">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("orSeparator")}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setShowLocalLogin(true)}
          >
            {t("signInWithUserPass")}
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Show local login form (with option to switch back to SSO)
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("titleLogin")}</CardTitle>
        <CardDescription>
          {t("descriptionLocal")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" aria-label={t("formAriaLabel")}>
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("username")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("usernamePlaceholder")}
                      autoComplete="username"
                      {...field}
                      disabled={isLoading}
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("password")}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("passwordPlaceholder")}
                      autoComplete="current-password"
                      {...field}
                      disabled={isLoading}
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isLoading} aria-busy={isLoading}>
              {isLoading ? t("submitting") : t("submit")}
            </Button>

            {useSSO && (
              <>
                <div className="relative" role="separator" aria-orientation="horizontal">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {t("orSeparator")}
                    </span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowLocalLogin(false);
                    onSSOLogin?.();
                  }}
                >
                  {t("signInWithSSO")}
                </Button>
              </>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

